import React, { useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight,
  ArrowUpRight,
  BadgeCheck,
  ShieldCheck,
  MessageSquare,
  Handshake,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Reveal } from "@/components/Reveal";
import { ContactForm } from "@/components/ContactForm";
import { VALUES } from "@/data/companies";
import { SERVICE_CARDS } from "@/data/intake";

const iconMap = { BadgeCheck, ShieldCheck, MessageSquare, Handshake };

export default function Home() {
  const [preset, setPreset] = useState({ category: "", nonce: 0 });

  const selectService = useCallback((category) => {
    setPreset((p) => ({ category, nonce: p.nonce + 1 }));
    const el = document.getElementById("contact-form");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  }, []);

  return (
    <div>
      {/* Hero */}
      <section className="relative overflow-hidden bg-slate-50 pt-32 md:pt-44 pb-20 md:pb-28">
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 opacity-[0.5]"
          style={{
            backgroundImage:
              "radial-gradient(circle at 20% 20%, rgba(37,99,235,0.06), transparent 40%)",
          }}
        />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-3xl">
            <motion.span
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-1.5 text-xs font-medium text-slate-600"
            >
              <span className="h-1.5 w-1.5 rounded-full bg-blue-600" />
              Edmonton, Alberta · Canadian Holding Company
            </motion.span>

            <motion.h1
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.05, ease: [0.22, 1, 0.36, 1] }}
              className="mt-7 font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-slate-900 leading-[1.05]"
            >
              Professional Services.
              <br />
              <span className="text-blue-600">Built on Trust.</span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
              className="mt-7 text-lg text-slate-600 leading-relaxed max-w-2xl"
            >
              AMS Venture Holdings Inc. owns and operates specialized businesses
              serving Alberta through ATM placement, professional moving
              services, and website & digital services.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.25, ease: [0.22, 1, 0.36, 1] }}
              className="mt-9 flex flex-col sm:flex-row gap-3"
            >
              <Button
                data-testid="hero-contact-cta"
                onClick={() => selectService("General Inquiry")}
                className="h-12 rounded-full bg-slate-900 hover:bg-blue-600 text-white px-7 text-base transition-colors duration-300"
              >
                Contact Us <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
              <Button
                asChild
                variant="outline"
                data-testid="hero-companies-cta"
                className="h-12 rounded-full border-slate-300 px-7 text-base hover:bg-white hover:border-slate-900 transition-colors duration-300"
              >
                <Link to="/companies">Explore Our Companies</Link>
              </Button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Service directory / intake hub */}
      <section className="max-w-7xl mx-auto px-6 lg:px-8 py-24 md:py-32">
        <Reveal>
          <div className="max-w-2xl">
            <p className="text-sm font-semibold uppercase tracking-widest text-blue-600">
              Service Directory
            </p>
            <h2 className="mt-3 font-heading text-3xl md:text-4xl font-bold tracking-tight text-slate-900">
              How can we help you today?
            </h2>
            <p className="mt-4 text-lg text-slate-600 leading-relaxed">
              Choose a service to start an inquiry. We'll route your request to
              the right team.
            </p>
          </div>
        </Reveal>

        <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {SERVICE_CARDS.map((c, i) => (
            <Reveal key={c.id} delay={i}>
              <div
                data-testid={`service-card-${c.id}`}
                className="group flex h-full flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white transition-[transform,box-shadow] duration-300 hover:-translate-y-1 hover:shadow-[0_20px_50px_rgb(0,0,0,0.08)]"
              >
                <div className="aspect-[16/10] overflow-hidden bg-slate-100">
                  <img
                    src={c.image}
                    alt={c.title}
                    loading="lazy"
                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                </div>
                <div className="flex flex-1 flex-col p-6">
                  <h3 className="font-heading text-lg font-semibold tracking-tight text-slate-900">
                    {c.title}
                  </h3>
                  <p className="mt-2 flex-1 text-sm text-slate-600 leading-relaxed">
                    {c.description}
                  </p>
                  {c.external ? (
                    <a
                      href={c.url}
                      data-testid={`service-cta-${c.id}`}
                      className="mt-5 inline-flex items-center gap-1 text-sm font-semibold text-slate-900 hover:text-blue-600 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 rounded"
                    >
                      {c.cta}
                      <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                    </a>
                  ) : (
                    <button
                      type="button"
                      data-testid={`service-cta-${c.id}`}
                      onClick={() => selectService(c.category)}
                      className="mt-5 inline-flex items-center gap-1 text-sm font-semibold text-slate-900 group-hover:text-blue-600 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 rounded"
                    >
                      {c.cta}
                      <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                    </button>
                  )}
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Why choose AMS */}
      <section className="bg-slate-50 py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-14">
            <Reveal className="lg:col-span-4">
              <p className="text-sm font-semibold uppercase tracking-widest text-blue-600">
                Why Choose AMS
              </p>
              <h2 className="mt-3 font-heading text-3xl md:text-4xl font-bold tracking-tight text-slate-900">
                The standard behind every business we run
              </h2>
              <p className="mt-4 text-lg text-slate-600 leading-relaxed">
                We build companies that Albertans can count on, guided by
                principles that never change.
              </p>
            </Reveal>

            <div className="lg:col-span-8 grid grid-cols-1 sm:grid-cols-2 gap-6">
              {VALUES.map((v, i) => {
                const Icon = iconMap[v.icon];
                return (
                  <Reveal key={v.title} delay={i}>
                    <div
                      data-testid={`value-${v.title.toLowerCase().replace(/\s+/g, "-")}`}
                      className="h-full rounded-2xl border border-slate-200 bg-white p-7 transition-shadow duration-300 hover:shadow-[0_12px_40px_rgb(0,0,0,0.05)]"
                    >
                      <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-blue-50">
                        <Icon className="h-5 w-5 text-blue-600" />
                      </div>
                      <h3 className="mt-5 font-heading text-lg font-semibold text-slate-900">
                        {v.title}
                      </h3>
                      <p className="mt-2 text-sm text-slate-600 leading-relaxed">
                        {v.body}
                      </p>
                    </div>
                  </Reveal>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Intake / contact form */}
      <section
        id="contact-form"
        className="scroll-mt-24 max-w-5xl mx-auto px-6 lg:px-8 py-24 md:py-28"
      >
        <Reveal>
          <div className="text-center max-w-2xl mx-auto">
            <p className="text-sm font-semibold uppercase tracking-widest text-blue-600">
              Start an Inquiry
            </p>
            <h2 className="mt-3 font-heading text-3xl md:text-4xl font-bold tracking-tight text-slate-900">
              Tell us what you need
            </h2>
            <p className="mt-4 text-lg text-slate-600 leading-relaxed">
              One form for every AMS service. Select a category and the right
              team will follow up.
            </p>
          </div>
        </Reveal>
        <Reveal delay={1}>
          <div className="mt-12">
            <ContactForm
              presetCategory={preset.category}
              presetNonce={preset.nonce}
            />
          </div>
        </Reveal>
      </section>
    </div>
  );
}
